import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "src/store";

import {
    AddRuleDialog,
    useAddRuleDialogChunk,
    usePreloadAddRuleDialog,
} from "@dashboard/policies/components/AddRuleDialog/AddRuleDialog.lazy";
import { usePolicyContentUpdate } from "@dashboard/policies/hooks/usePolicyContentUpdate/usePolicyContentUpdate";
import {
    CollapsibleProvider,
    CollapsibleSectionDetails,
    CollapsibleSectionSummary,
    CollapsibleSectionTitle,
} from "@dashboard/shared/components/CollapsibleSection";
import type { PolicyRulesUpdate } from "@dashboard/shared/components/rules/PolicyRuleItem/PolicyRuleItem";
import PolicyRulesTable from "@dashboard/shared/components/rules/PolicyRulesTable/PolicyRulesTable";
import { selectDevicesByGroupIds } from "@devices/store/selectors/devices";
import { MdmPolicyCommand } from "@devices/types";
import { SecondaryButton } from "@shared/CustomButton";

type Props = {
    policy: API.PolicyViewModel | null;
    readOnly?: boolean;
};

export const CommandsSection = ({ policy, readOnly = false }: Props) => {
    const [isAddCommandDialogOpen, setIsAddCommandDialogOpen] = React.useState(false);
    const { isLoading: isDialogLoading, load: loadDialog } = useAddRuleDialogChunk();
    usePreloadAddRuleDialog(!readOnly);

    const { t } = useTranslation(["pages", "common"]);

    const { updateContent, isLoading } = usePolicyContentUpdate();

    const content = policy?.content ?? [];
    const groupIds = policy?.groupIds ?? [];

    const policyGroupDevices = useSelector((state) => selectDevicesByGroupIds(state, groupIds));

    const onUpdate: PolicyRulesUpdate = (updatedContent, options) =>
        policy ? updateContent({ id: policy.id, name: policy.name, content: updatedContent }, options) : Promise.resolve(false);

    const openAddCommandDialog = async () => {
        if (await loadDialog()) {
            setIsAddCommandDialogOpen(true);
        }
    };

    const appendCommandAndSave = (json: Record<string, unknown>) => onUpdate([...content, json as MdmPolicyCommand]);

    const getTooltipProps = () => {
        if (isDialogLoading) {
            return { title: t("common:tooltip.featureLoading"), hide: false };
        }

        if (readOnly) {
            return { title: t("common:tooltip.cannotManagePolicies"), hide: false };
        }

        if (!policy) {
            return { title: t("pages:policiesPage.tooltip.noPolicySelected"), hide: false };
        }

        return { title: "", hide: true };
    };

    return (
        <CollapsibleProvider initialValue disabled={!policy}>
            <CollapsibleSectionSummary
                action={
                    <SecondaryButton
                        startIcon={<i className="fa-solid fa-plus" />}
                        onClick={openAddCommandDialog}
                        disabled={!policy || readOnly || isDialogLoading}
                        loading={isDialogLoading}
                        tooltipProps={getTooltipProps()}
                    >
                        {t("pages:policiesPage.button.addRule")}
                    </SecondaryButton>
                }
            >
                <CollapsibleSectionTitle>{t("pages:policiesPage.section.rules.title")}</CollapsibleSectionTitle>
            </CollapsibleSectionSummary>
            <CollapsibleSectionDetails>
                <PolicyRulesTable
                    content={content}
                    onUpdate={readOnly ? undefined : onUpdate}
                    loading={isLoading}
                    readOnly={readOnly}
                />
            </CollapsibleSectionDetails>
            {policy && isAddCommandDialogOpen ? (
                <AddRuleDialog
                    title={t("pages:policiesPage.button.addRule")}
                    devices={policyGroupDevices}
                    toggleDialog={() => setIsAddCommandDialogOpen(false)}
                    createCommand={appendCommandAndSave}
                    loading={isLoading}
                />
            ) : null}
        </CollapsibleProvider>
    );
};
